import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';

export interface ServiceVisit {
  id: string;
  property_id: string;
  property_name: string;
  visit_type: string;
  status: string;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  technician_user_id: string | null;
  technician_name: string | null;
  summary: string | null;
  resolved_issue_ids: string[];
  created_at: string;
}

export interface VisitDetail {
  id: string;
  property_id: string;
  organization_id: string;
  visit_type: string;
  status: string;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  technician_user_id: string | null;
  technician_name: string | null;
  route_zone: string | null;
  mileage_one_way: number | null;
  summary: string | null;
  internal_notes: string | null;
  customer_notes: string | null;
  resolved_issue_ids: string[];
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface VisitChecklist {
  id: string;
  visit_id: string;
  template_id: string | null;
  template_type: string;
  items: ChecklistItem[];
  completed_at: string | null;
}

export interface ChecklistItem {
  id: string;
  label: string;
  required: boolean;
  helper_text: string | null;
  status: 'pending' | 'pass' | 'fail' | 'na' | 'flag';
  notes: string | null;
  photo_paths: string[];
}

export interface VisitFinding {
  id: string;
  visit_id: string;
  category: string;
  severity: string;
  title: string;
  description: string | null;
  parts_used: string | null;
  recommendations: string | null;
  photo_paths: string[];
  created_issue_id: string | null;
  created_at: string;
  created_by: string;
}

const VISITS_KEY = 'visits';
const VISIT_DETAIL_KEY = 'visit-detail';
const VISIT_CHECKLISTS_KEY = 'visit-checklists';
const VISIT_FINDINGS_KEY = 'visit-findings';

export function useVisits(filters?: { status?: string | string[]; propertyId?: string }) {
  const { organization } = useApp();
  const orgId = organization?.id;

  // Normalize status to TEXT[] for the RPC
  const statusArray = filters?.status
    ? Array.isArray(filters.status) ? filters.status : [filters.status]
    : null;

  return useQuery({
    queryKey: [VISITS_KEY, orgId, statusArray, filters?.propertyId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_visits', {
        p_org_id: orgId!,
        p_status: statusArray,
        p_property_id: filters?.propertyId ?? null,
      });
      if (error) throw error;
      return (data ?? []) as ServiceVisit[];
    },
    enabled: !!orgId,
  });
}

export function useVisitDetail(visitId: string | undefined) {
  return useQuery({
    queryKey: [VISIT_DETAIL_KEY, visitId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('service_visits')
        .select('*')
        .eq('id', visitId!)
        .single();
      if (error) throw error;
      return data as unknown as VisitDetail;
    },
    enabled: !!visitId,
  });
}

export function useVisitChecklists(visitId: string | undefined) {
  return useQuery({
    queryKey: [VISIT_CHECKLISTS_KEY, visitId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('visit_checklists')
        .select('*')
        .eq('visit_id', visitId!)
        .order('template_type');
      if (error) throw error;
      return (data ?? []) as unknown as VisitChecklist[];
    },
    enabled: !!visitId,
  });
}

export function useVisitFindings(visitId: string | undefined) {
  return useQuery({
    queryKey: [VISIT_FINDINGS_KEY, visitId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('visit_findings')
        .select('*')
        .eq('visit_id', visitId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as VisitFinding[];
    },
    enabled: !!visitId,
  });
}

export function useScheduleVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      propertyId: string;
      visitType: string;
      scheduledAt: string;
      technicianUserId?: string;
      technicianName?: string;
      resolvedIssueIds?: string[];
      internalNotes?: string;
    }) => {
      const { data, error } = await supabase.rpc('schedule_visit', {
        p_property_id: params.propertyId,
        p_visit_type: params.visitType,
        p_scheduled_at: params.scheduledAt,
        p_technician_user_id: params.technicianUserId ?? null,
        p_technician_name: params.technicianName ?? null,
        p_resolved_issue_ids: params.resolvedIssueIds ?? null,
        p_internal_notes: params.internalNotes ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [VISITS_KEY] });
    },
  });
}

export function useStartVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (visitId: string) => {
      const { data, error } = await supabase.rpc('start_visit', {
        p_visit_id: visitId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_data, visitId) => {
      qc.invalidateQueries({ queryKey: [VISITS_KEY] });
      qc.invalidateQueries({ queryKey: [VISIT_DETAIL_KEY, visitId] });
      qc.invalidateQueries({ queryKey: [VISIT_CHECKLISTS_KEY, visitId] });
    },
  });
}

export function useCompleteVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      visitId: string;
      summary?: string;
      customerNotes?: string;
    }) => {
      const { data, error } = await supabase.rpc('complete_visit', {
        p_visit_id: params.visitId,
        p_summary: params.summary ?? null,
        p_customer_notes: params.customerNotes ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_data, params) => {
      qc.invalidateQueries({ queryKey: [VISITS_KEY] });
      qc.invalidateQueries({ queryKey: [VISIT_DETAIL_KEY, params.visitId] });
    },
  });
}

export function useUpdateChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { checklistId: string; items: ChecklistItem[]; visitId: string }) => {
      const { error } = await supabase
        .from('visit_checklists')
        .update({ items: params.items as unknown as Record<string, unknown>[] })
        .eq('id', params.checklistId);
      if (error) throw error;
    },
    onSuccess: (_data, params) => {
      qc.invalidateQueries({ queryKey: [VISIT_CHECKLISTS_KEY, params.visitId] });
    },
  });
}

export function useCreateFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      visitId: string;
      category: string;
      severity: string;
      title: string;
      description?: string;
      partsUsed?: string;
      recommendations?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('visit_findings')
        .insert({
          visit_id: params.visitId,
          category: params.category,
          severity: params.severity,
          title: params.title,
          description: params.description ?? null,
          parts_used: params.partsUsed ?? null,
          recommendations: params.recommendations ?? null,
          created_by: user.id,
        })
        .select('id')
        .single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: (_data, params) => {
      qc.invalidateQueries({ queryKey: [VISIT_FINDINGS_KEY, params.visitId] });
    },
  });
}

export function useUploadVisitPhoto() {
  return useMutation({
    mutationFn: async (params: {
      orgId: string;
      propertyId: string;
      visitId: string;
      file: File;
    }) => {
      const ext = params.file.name.split('.').pop() ?? 'jpg';
      const filename = `${crypto.randomUUID()}.${ext}`;
      const path = `${params.orgId}/${params.propertyId}/${params.visitId}/${filename}`;
      const { error } = await supabase.storage
        .from('service-photos')
        .upload(path, params.file, { contentType: params.file.type });
      if (error) throw error;
      return path;
    },
  });
}
